/**
 * 채팅 턴 계측의 단계별 조각 — 턴 시작 · 스트림 이벤트 1건 · 종료 사유 판정 · 결과 발화.
 * 조립은 `trackAiBuilderTurnAnalyticsEvents` 가 한다.
 */
import { trackEvent } from '@zep/logger/tracking';

import { type AppliedGenerationCounter } from './aiGenerationCompletedTracking';
import { type AiBuilderTrackedTurnIntent } from './events.quiz-ai-builder';
import {
  type AiBuilderChatOrchestrationEvent,
  type AiBuilderChatQuestionSnapshotReplaceEvent,
  type AiBuilderTurnRequest,
} from './types';

/** 생성 완료로 세는 문항 변경 — 되돌리기·삭제 등은 생성이 아니다. */
const GENERATION_CHANGE_TYPES: ReadonlySet<
  AiBuilderChatQuestionSnapshotReplaceEvent['change_type']
> = new Set(['create', 'replace']);

/** 턴 시작을 남기고, 턴 동안 이어 쓸 식별자를 돌려준다. */
export const trackTurnStart = (request: AiBuilderTurnRequest) => {
  const sourceTypes = request.context.sourceTypes ?? [];
  const intent: AiBuilderTrackedTurnIntent =
    request.analyticsIntent ?? request.intent.type;
  const sessionId = request.context.sessionId ?? '';

  trackEvent('quiz_ai_builder_turn_start', {
    intent,
    decision: request.intent.type === 'confirm' ? request.intent.decision : '',
    // sessionId 유무는 첫 턴 신호가 아니다 — 패널 오픈 시 세션이 선생성된다.
    // requestId 는 대화 시작(restart 포함)마다 1부터 증가하므로 1 이 곧 첫 턴.
    is_session_first: request.requestId === 1,
    session_id: sessionId,
    client_request_id: request.clientRequestId,
    draft_id: request.context.draftInstanceId,
    source_count: request.context.sourceRefs.length,
    has_file_source: sourceTypes.includes('file'),
    has_youtube_source: sourceTypes.includes('youtube'),
    has_website_source: sourceTypes.includes('website'),
    has_selected_question: request.context.selectedQuestionContext !== null,
  });

  return { intent, sessionId };
};

type TurnStreamContext = {
  sessionId: string;
  clientRequestId: string;
  draftId: string;
  generations: AppliedGenerationCounter;
};

/**
 * 소비자가 적용을 끝낸 스트림 이벤트 1건을 기록한다 — 문항 변경·선택지 노출을 발화하고 생성 완료 재료를 모은다.
 * 바뀐 문항 수와, 서버 오류 메시지였다면 그 사유를 돌려준다.
 */
export const trackTurnStreamEvent = (
  event: AiBuilderChatOrchestrationEvent,
  { sessionId, clientRequestId, draftId, generations }: TurnStreamContext,
): { changedCount: number; failureReason: string | null } => {
  if (event.type === 'replaceQuestionSnapshots') {
    const changedCount = event.changed_question_ids.length;
    trackEvent('quiz_ai_builder_question_change', {
      change_type: event.change_type,
      changed_count: changedCount,
      total: event.total,
      generation_id: event.generationId ?? '',
      session_id: sessionId,
      client_request_id: clientRequestId,
      draft_id: draftId,
    });
    if (GENERATION_CHANGE_TYPES.has(event.change_type)) {
      generations.add(event.generationId, changedCount);
    }
    return { changedCount, failureReason: null };
  }

  if (event.type === 'applyGeneratedQuizzes') {
    generations.add(event.generationId, event.quizzes.length);
    return { changedCount: event.quizzes.length, failureReason: null };
  }

  if (event.type !== 'append' && event.type !== 'replace') {
    return { changedCount: 0, failureReason: null };
  }

  const { message } = event;
  if (message.kind === 'choice' || message.kind === 'confirm') {
    trackEvent('quiz_ai_builder_choice_view', {
      choice_kind: message.kind,
      option_count: message.kind === 'choice' ? message.options.length : 2,
      session_id: sessionId,
      client_request_id: clientRequestId,
    });
  }
  const isServerError = message.kind === 'system' && message.tone === 'error';
  return {
    changedCount: 0,
    failureReason: isServerError ? message.errorCode ?? 'server_error' : null,
  };
};

/**
 * 스트림이 끝난 뒤 실패 사유를 정한다. 사유가 이미 있으면 그대로, 없으면 중단(aborted) →
 * 스트림을 다 받지 못한 소비자 오류(consumer_error) 순이다. 끝까지 정상이면 null.
 */
export const resolveTurnFailureReason = ({
  failureReason,
  aborted,
  streamConsumed,
}: {
  failureReason: string | null;
  aborted: boolean;
  streamConsumed: boolean;
}) => {
  if (failureReason) return failureReason;
  if (aborted) return 'aborted';
  return streamConsumed ? null : 'consumer_error';
};

/** 턴 결과 1회 — 유저 중단은 실패가 아니라 abort 로 분리해 에러율을 오염시키지 않는다. */
export const trackTurnResult = (
  failureReason: string | null,
  result: {
    intent: AiBuilderTrackedTurnIntent;
    sessionId: string;
    clientRequestId: string;
    latencyMs: number;
    questionChangedCount: number;
  },
) => {
  if (failureReason === 'aborted') {
    trackEvent('quiz_ai_builder_turn_abort', {
      intent: result.intent,
      session_id: result.sessionId,
      client_request_id: result.clientRequestId,
      latency_ms: result.latencyMs,
    });
  } else if (failureReason) {
    trackEvent('quiz_ai_builder_turn_error', {
      intent: result.intent,
      session_id: result.sessionId,
      client_request_id: result.clientRequestId,
      latency_ms: result.latencyMs,
      failure_reason: failureReason,
    });
  } else {
    trackEvent('quiz_ai_builder_turn_end', {
      intent: result.intent,
      session_id: result.sessionId,
      client_request_id: result.clientRequestId,
      latency_ms: result.latencyMs,
      question_changed_count: result.questionChangedCount,
    });
  }
};
